import { makeAutoObservable } from "mobx";

import type { Nullable, MathNumber } from "models-webgl2";
import { isNumber } from "../is-number";
import { lerpRounded } from "../lerp-rounded";
import { normalizeClamped } from "../normalize-clamped";
import { toDegrees } from "../to-degrees";
import { toRadians } from "../to-radians";
import { OnAnimation, OnAnimationEnd } from "./core-math.types";

export class CoreMath {
  private _value: number;
  public get value() {
    return this._value;
  }
  public set value(value: number) {
    // Prohibit change value during animation pending.
    if (this._animationFrameId) return;
    this._value = value;
  }

  private _valueAnimatingTo: Nullable<number> = null;
  public get valueAnimatingTo(): number {
    return this._valueAnimatingTo ?? this.value;
  }
  public set valueAnimatingTo(value: Nullable<number>) {
    this._valueAnimatingTo = value;
  }

  private _animationFrameId = 0;
  private _startTime = 0;

  private _onAnimation: Nullable<OnAnimation>;
  private _onAnimationEnd: Nullable<OnAnimationEnd>;

  constructor(value: number) {
    this.value = value;
    makeAutoObservable(this);
  }

  public clone() {
    return new CoreMath(this.value);
  }

  public copy(from: MathNumber): boolean {
    if (this._animationFrameId) return false;
    const value = isNumber(from) ? from : from._value;
    const equal = value === this.value;
    this.set(value);
    return !equal;
  }

  public valueOf() {
    return this.value;
  }

  public set(value: number) {
    this.value = value;
    return this;
  }

  public zero() {
    this.set(0);
    return this;
  }

  public add(value: number) {
    this.set(this.value + value);
    return this;
  }

  public sub(value: number) {
    this.set(this.value - value);
    return this;
  }

  public modulo(value: number) {
    this.set(this.value % value);
    return this;
  }

  public negate() {
    this.set(-this.value);
    return this;
  }

  public abs() {
    this.set(Math.abs(this.value));
    return this;
  }

  public percentage(percent: number) {
    this.set((this.value * percent) / 100);
    return this;
  }

  public lerp(to: number, ms: number, decimalPoint?: number) {
    this.stopAnimation();
    this._animationFrameId = requestAnimationFrame((now) => {
      this._animate(this.value, to, ms, now, decimalPoint);
    });
    return this;
  }

  public stopAnimation() {
    cancelAnimationFrame(this._animationFrameId);
    this.valueAnimatingTo = null;
    this._animationFrameId = 0;
    this._startTime = 0;
  }

  // Trigonometry ↓
  public getRadians() {
    return toRadians(this.value);
  }

  public getDegrees() {
    return toDegrees(this.value);
  }

  public toRadians() {
    this.value = toRadians(this.value);
    return this;
  }

  public toDegrees() {
    this.value = toDegrees(this.value);
    return this;
  }

  public sin() {
    this.value = Math.sin(this.value);
    return this;
  }

  public cos() {
    this.value = Math.cos(this.value);
    return this;
  }

  // Trigonometry ↑

  // Callbacks ↓
  public onAnimation(callback: OnAnimation) {
    this._onAnimation = callback;
    return this;
  }

  public onAnimationEnd(callback: OnAnimationEnd) {
    this._onAnimationEnd = callback;
    return this;
  }

  // Callbacks ↑

  private _animate(from: number, to: number, by: number, now: number, decimalPoint = 4) {
    this.valueAnimatingTo = to;

    if (!this._startTime) this._startTime = now;

    const elapsed = now - this._startTime;

    const t = normalizeClamped(elapsed, 0, by);

    // Important value is set by "_value" to not go through set accessor
    this._value = lerpRounded(from, to, t, decimalPoint);

    this._onAnimation?.(this.value, t);

    if (elapsed >= by) {
      this._onAnimationEnd?.(this.value);
      this.stopAnimation();
      return;
    }

    this._animationFrameId = requestAnimationFrame((now) => {
      this._animate(from, to, by, now, decimalPoint);
    });
  }

  public static zero(): CoreMath {
    return new CoreMath(0);
  }

  public static one(): CoreMath {
    return new CoreMath(1);
  }

  public static fromValue(value: MathNumber): CoreMath {
    value = isNumber(value) ? value : value._value;
    return new CoreMath(value);
  }
}

export default CoreMath;
